import { gql } from '@apollo/client';

import axios from '../../services/axios';
import type { Category } from '../../types/api';
import { TopicListItemActionsFragment, TopicListItemFragment } from './topic';

type ProposeCategoryData = {
  _id: string;
  author: string;
  website: string;
  type: 'category';
  content: string;
  description: string;
  status: 'pending';
};

export const proposeCategory = async (content: string, headingUid?: number | null) => {
  try {
    const { data } = await axios.post<ProposeCategoryData>(`/api/tag/category/proposal`, {
      content,
      // B2_01 — en mode média, rattache le tag proposé à la rubrique « Lecteurs » du cercle
      // sélectionné (uid stable de la rubrique). Absent hors média / hors cercle → comportement
      // actuel (tag rattaché au website seul, côté API).
      ...(headingUid != null ? { headingUid } : {}),
    });
    return { ...data, id: data._id, slug: data.content, title: content } as Category;
  } catch (e) {
    console.error(e);
    return;
  }
};

type ProposeFacetData = {
  _id: string;
  author: string;
  website: string;
  type: 'theme' | 'entity';
  content: string;
  title: string;
  description: string;
  status: 'approved' | 'pending';
};

// B2_07 — issues possibles d'une proposition de facette. Contrairement à proposeCategory
// (qui avale tout en `undefined`), l'appelant DOIT distinguer les cas : un tag existant
// `pending` ne doit jamais rejoindre les pills (resolveFacetIds écarte silencieusement tout
// id non-approved au submit — la pill disparaîtrait sans erreur), et un doublon `deleted`
// (409) a son message dédié.
export type ProposeFacetResult =
  | { outcome: 'approved'; facet: Category }
  | { outcome: 'pending' }
  | { outcome: 'deleted' }
  | { outcome: 'error' };

// B2_07 — création/proposition d'un tag theme/entity depuis l'éditeur staff (endpoint B1_07,
// miroir de proposeCategory sans le rattachement heading — les facettes vivent hors rubriques).
// Staff éditorial → le tag revient `approved` (D25=A) et est immédiatement attachable.
export const proposeFacet = async (
  facetType: 'theme' | 'entity',
  content: string,
): Promise<ProposeFacetResult> => {
  try {
    // Custom config: `noError` to avoid showing the error toast — le handler global
    // (MainLayout applyErrorHandler) afficherait le message serveur brut (ex. 409) en
    // doublon du toast i18n dédié de FacetSelector.
    const { data } = await axios.post<ProposeFacetData>(
      `/api/tag/${facetType}/proposal`,
      { content },
      { noError: true } as any,
    );
    if (data?._id && data.status === 'approved') {
      return {
        outcome: 'approved',
        facet: { ...data, id: data._id, slug: data.content, title: data.title } as Category,
      };
    }
    if (data?._id && data.status === 'pending') {
      return { outcome: 'pending' };
    }
    // Corps inattendu (ex. quirk legacy 200 + gcCode 410 sur session expirée) : erreur générique.
    return { outcome: 'error' };
  } catch (e) {
    console.error(e);
    const status = (e as { response?: { status?: number } })?.response?.status;
    return status === 409 ? { outcome: 'deleted' } : { outcome: 'error' };
  }
};

export const CategoryBadgeFragment = gql`
  fragment CategoryBadgeFragment on Category {
    slug
    title
  }
`;

export const CategoryDetailsFragment = gql`
  fragment CategoryDetailsFragment on Category {
    id
    description
    ...CategoryBadgeFragment
    count {
      allTopics
      lastDayTopics
      last7daysTopics
    }
  }
  ${CategoryBadgeFragment}
`;

// B2_04 — autocomplétion des facettes theme/entity (éditeur staff média).
// Même forme de retour que categorySearch (type CategorySearch réutilisé côté API).
export const facetSearchQuery = gql`
  query facetSearchQuery($facetType: String!, $page: Int, $limit: Int, $search: String) {
    website {
      id
      name
      facetSearch(facetType: $facetType, search: $search, page: $page, limit: $limit) {
        categories {
          id
          ...CategoryDetailsFragment
        }
      }
    }
  }
  ${CategoryDetailsFragment}
`;

// Suggestion LLM d'étiquettes — tout membre en média (design 2026-07-21, étendu au
// staff le 2026-07-30). Auth membre requise côté API ; rate-limit 3/min (erreur
// GraphQL → UI non bloquante).
export interface TagSuggestionsRes {
  website?: {
    tagSuggestions?: {
      existing?: (Category | null)[] | null;
      proposals?: (string | null)[] | null;
    } | null;
  } | null;
}

export const tagSuggestionsQuery = gql`
  query tagSuggestionsQuery($title: String, $content: String!) {
    website {
      id
      tagSuggestions(title: $title, content: $content) {
        existing {
          id
          title
          slug
        }
        proposals
      }
    }
  }
`;

export const allCategoriesQuery = gql`
  query allTagsQuery($page: Int, $limit: Int, $search: String) {
    website {
      id
      name
      categorySearch(search: $search, page: $page, limit: $limit) {
        categories {
          id
          ...CategoryDetailsFragment
        }
      }
    }
  }
  ${CategoryDetailsFragment}
`;

export const getCategoryQuery = gql`
  query getCategoryQuery($category: String) {
    website {
      id
      name
      categoryLoad(slug: $category) {
        id
        slug
        title
        description
        adult
        status
        isAdsSafe
        ...CategoryBadgeFragment
      }
    }
  }
  ${CategoryBadgeFragment}
`;

export const topicsTagQuery = gql`
  query topicsTagQuery(
    $page: Int
    $limit: Int
    $dateStart: GraphQLDateTime
    $dateEnd: GraphQLDateTime
    $sort: String
    $category: String
    $skipContent: Boolean = false
  ) {
    website {
      id
      name
      categoryLoad(slug: $category) {
        id
        slug
        title
        description
        adult
        status
        isAdsSafe
        ...CategoryBadgeFragment
      }
      topicList(
        page: $page
        limit: $limit
        dateStart: $dateStart
        dateEnd: $dateEnd
        sort: $sort
        category: $category
      ) {
        meta {
          count
          page
          limit
        }
        topics {
          id
          ...TopicListItemFragment
          ...TopicListItemActionsFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
  ${TopicListItemActionsFragment}
  ${CategoryBadgeFragment}
`;

// B4_05 — navigation par facette. Quasi-clones de topicsTagQuery : `facetLoad` est
// ALIASÉ en `categoryLoad` pour réutiliser sans modification la machinerie de la page
// de tag (redux topics-slice `setTagInfo` lit `website.categoryLoad`, render commun).
// Le type de facette est figé dans la query (theme/entity), l'arg `topicList` adéquat
// reçoit le même slug. Voir FacetTopicsPage.
export const topicsThemeQuery = gql`
  query topicsThemeQuery(
    $page: Int
    $limit: Int
    $dateStart: GraphQLDateTime
    $dateEnd: GraphQLDateTime
    $sort: String
    $slug: String
    $skipContent: Boolean = false
  ) {
    website {
      id
      name
      categoryLoad: facetLoad(slug: $slug, type: "theme") {
        id
        slug
        title
        description
        adult
        status
        isAdsSafe
        ...CategoryBadgeFragment
      }
      topicList(
        page: $page
        limit: $limit
        dateStart: $dateStart
        dateEnd: $dateEnd
        sort: $sort
        theme: $slug
      ) {
        meta {
          count
          page
          limit
        }
        topics {
          id
          ...TopicListItemFragment
          ...TopicListItemActionsFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
  ${TopicListItemActionsFragment}
  ${CategoryBadgeFragment}
`;

export const topicsEntityQuery = gql`
  query topicsEntityQuery(
    $page: Int
    $limit: Int
    $dateStart: GraphQLDateTime
    $dateEnd: GraphQLDateTime
    $sort: String
    $slug: String
    $skipContent: Boolean = false
  ) {
    website {
      id
      name
      categoryLoad: facetLoad(slug: $slug, type: "entity") {
        id
        slug
        title
        description
        adult
        status
        isAdsSafe
        ...CategoryBadgeFragment
      }
      topicList(
        page: $page
        limit: $limit
        dateStart: $dateStart
        dateEnd: $dateEnd
        sort: $sort
        entity: $slug
      ) {
        meta {
          count
          page
          limit
        }
        topics {
          id
          ...TopicListItemFragment
          ...TopicListItemActionsFragment
        }
      }
    }
  }
  ${TopicListItemFragment}
  ${TopicListItemActionsFragment}
  ${CategoryBadgeFragment}
`;
